import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import { useAuth } from '@/features/auth/hooks/useAuth';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function usePushSubscription() {
  const { user, workerContext } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check current state on mount — use registration.pushManager (iOS exposes push only via SW registration)
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
      setPermission('unsupported');
      setIsLoading(false);
      return;
    }

    navigator.serviceWorker.ready
      .then(async (reg) => {
        if (!reg.pushManager) {
          setPermission('unsupported');
          setIsLoading(false);
          return;
        }
        setPermission(Notification.permission);
        const sub = await reg.pushManager.getSubscription();
        setIsSubscribed(!!sub);
        setIsLoading(false);
      })
      .catch(() => {
        setPermission('unsupported');
        setIsLoading(false);
      });
  }, []);

  const subscribe = useCallback(async () => {
    if (!user) return;

    try {
      setIsLoading(true);

      const reg = await navigator.serviceWorker.ready;
      if (!reg.pushManager) {
        setIsLoading(false);
        return;
      }

      // Must be called from user gesture on iOS
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        setIsLoading(false);
        return;
      }
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
      });

      const subJSON = subscription.toJSON();
      if (!subJSON.endpoint || !subJSON.keys) {
        throw new Error('Invalid subscription object');
      }

      // Resolve the tenant-scoped path at call time — reading COLLECTIONS at module
      // scope would freeze the '_' fallback before the session tenant is set.
      const pushSubsPath = COLLECTIONS.team.pushSubscriptions;

      // Create the new subscription FIRST, then delete stale ones. The reverse order
      // (delete-then-create) had a failure window: if the addDoc step failed after the
      // deletes succeeded, the worker ended up with zero subs while their device-level
      // permission stayed granted — looking subscribed but receiving nothing.
      const colRef = collection(db, ...splitPath(pushSubsPath));
      await addDoc(colRef, {
        workerAuthUid: user.uid,
        workerName: workerContext?.workerName || '',
        endpoint: subJSON.endpoint,
        keys: {
          p256dh: subJSON.keys.p256dh,
          auth: subJSON.keys.auth,
        },
        createdAt: serverTimestamp(),
        userAgent: navigator.userAgent,
      });

      // Delete any prior subscriptions for this worker that aren't the one we just wrote
      // (Firestore can't `!=`, so query by workerAuthUid and filter client-side).
      const allSubs = await getDocs(query(colRef, where('workerAuthUid', '==', user.uid)));
      await Promise.all(
        allSubs.docs
          .filter((d) => (d.data() as { endpoint?: string }).endpoint !== subJSON.endpoint)
          .map((d) => deleteDoc(doc(db, ...splitPath(pushSubsPath), d.id)))
      );

      setIsSubscribed(true);
    } catch (err) {
      console.error('[Push] Subscription failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, workerContext]);

  return { permission, isSubscribed, isLoading, subscribe };
}
